import { Theme } from '@eversports/react-components/design-tokens/theme'
import DateTimeRange from '@eversports/react-components/primitives/DateTimeRange'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { Localized } from '../../../localization/react'
import { ShopProduct } from '../../../typings/Product'
import Venue from '../../../typings/Venue'
import CardUsagesInformation from '../../Product/CardUsagesInformation'
import CardValidityInformation from '../../Product/CardValidityInformation'
import ProductInformation from '../../Product/ProductInformation'
import VoucherConditions from '../../Product/VoucherConditions'

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontSize: theme.fontSizing.large,
    [theme.breakpoints.down('md')]: {
      fontSize: theme.fontSizing.default,
    },
  },
}))

interface Props {
  product: ShopProduct
  venue: Venue
}

const ProductBookingDetailsContentInformation: React.FC<React.PropsWithChildren<Props>> = ({ product, venue }) => {
  switch (product.__typename) {
    case 'Card':
      return (
        <Flex flexDirection="column">
          <Flex horizontalSpacing="dense">
            <Text fontWeight="bold">{product.name}</Text>
            <ProductInformation product={product} />
          </Flex>
          <Text size="small" marginBottom="dense">
            {venue.name}
          </Text>
          <Flex flexDirection="column" verticalSpacing="dense">
            {product.usages.length > 0 && (
              <CardUsagesInformation
                usages={product.usages}
                unlimitedUsages={product.unlimitedUsages}
                cardType={product.cardType}
              />
            )}
            {product.validity && <CardValidityInformation validity={product.validity} />}
          </Flex>
        </Flex>
      )
    case 'Membership':
      return (
        <Flex flexDirection="column">
          <Flex horizontalSpacing="dense">
            <Text fontWeight="bold">{product.name}</Text>
            <ProductInformation product={product} />
          </Flex>
          <Text size="small" marginBottom="dense">
            {venue.name}
          </Text>
          <Flex flexDirection="column" verticalSpacing="dense">
            <div>
              <Text size="small">
                <Localized id="period-of-validity" />
              </Text>
              <Text>
                <Localized
                  id={product.runtime.renewal ? 'membership-min-runtime' : 'membership-runtime'}
                  params={{
                    interval: (
                      <Localized
                        id="interval-including-single-number"
                        params={{
                          intervalValue: product.runtime.initial.value,
                          intervalType: product.runtime.initial.type as any,
                        }}
                      />
                    ),
                  }}
                />
              </Text>
            </div>
            <div>
              <Text size="small">
                <Localized id="starting-date" />
              </Text>
              <Text>
                <Localized id="membership-starting-date" params={{ startingDate: new Date() }} />
              </Text>
            </div>
          </Flex>
        </Flex>
      )
    case 'Voucher':
      return (
        <Flex flexDirection="column">
          <Flex flexDirection="column">
            <div>
              <Text fontWeight="bold">{product.name}</Text>
              <Text size="small" marginTop="dense">
                {venue.name}
              </Text>
              {product.conditions && <VoucherConditions conditions={product.conditions} />}
            </div>
            {product.validityInterval && (
              <Flex marginTop="wide">
                <Icon icon={Icons.CALENDAR} marginRight="dense" />
                <Text fontWeight="bold">
                  <Localized
                    id="valid-for-interval"
                    params={{
                      interval: (
                        <Localized
                          id="interval-including-single-number"
                          params={{
                            intervalValue: product.validityInterval.value,
                            intervalType: product.validityInterval.type as any,
                          }}
                        />
                      ),
                    }}
                  />
                </Text>
              </Flex>
            )}
          </Flex>
          <Text marginTop="wide">
            <Localized id="voucher-purchase-information" />
          </Text>
        </Flex>
      )
    case 'Recording':
      return (
        <Flex flexDirection="column" verticalSpacing="default">
          <div>
            <Text fontWeight="bold">{product.name}</Text>
            <Text>{venue.name}</Text>
          </div>
          {product.recordingStartDate && product.recordingEndDate && (
            <div>
              <Text>
                <Localized id="recording-original-streaming-time" />
              </Text>
              <Text>
                <DateTimeRange start={product.recordingStartDate} end={product.recordingEndDate} />
              </Text>
            </div>
          )}
          {product.trainer && (
            <div>
              <Text>
                <Localized id="recording-trainer" />
              </Text>
              <Text>{product.trainer.nickname}</Text>
            </div>
          )}
          {product.availableUntil && (
            <div>
              <Text>
                <Localized id="recording-available-until" />
              </Text>
              <Text>
                <DateTimeRange start={product.availableUntil} end={product.availableUntil} />
              </Text>
            </div>
          )}
        </Flex>
      )
    default:
      return assertNever(product)
  }
}

const ProductBookingDetailsContent: React.FC<React.PropsWithChildren<Props>> = ({ product, venue }) => {
  const classes = useStyles()
  const isProductARecording = product.__typename === 'Recording'

  return (
    <Flex flexDirection="column" verticalSpacing={['dense', 'wide']}>
      <Heading is="h2" className={classes.heading} marginTop={['default', 'default', 'none']}>
        {isProductARecording ? <Localized id="recording-details" /> : <Localized id="product-details" />}
      </Heading>
      <ProductBookingDetailsContentInformation product={product} venue={venue} />
    </Flex>
  )
}

export default ProductBookingDetailsContent
